import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { walkyAccountsAPI, type WalkyAccount } from "../api";
import { useAuthStore } from "../store/authStore";
import { BadgeCheck, KeyRound, Loader2, RefreshCw } from "lucide-react";

/**
 * The per-school official Walky accounts.
 *
 * One `walkyofficial@<school domain>` per school. Staff sign into these to post
 * as Walky inside a single school, so this page is where they are created and
 * where their passwords are set — email-based reset is closed for them on
 * purpose.
 */
export function WalkyAccountsPage() {
  const { isAuthenticated } = useAuthStore();
  const queryClient = useQueryClient();

  const [target, setTarget] = useState<WalkyAccount | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const { data: accounts, isLoading } = useQuery({
    queryKey: ["walky-accounts"],
    queryFn: walkyAccountsAPI.list,
    enabled: isAuthenticated,
  });

  const syncMutation = useMutation({
    mutationFn: walkyAccountsAPI.sync,
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["walky-accounts"] });
      const parts = [`${result.created} created`, `${result.skipped} already existed`];
      if (result.failed.length) {
        // Naming them matters: the fix is to give the school an email domain.
        parts.push(`skipped (no email domain): ${result.failed.join(", ")}`);
      }
      setFeedback({ type: "success", message: parts.join(" · ") });
    },
    onError: () =>
      setFeedback({ type: "error", message: "Could not sync the accounts." }),
  });

  const passwordMutation = useMutation({
    mutationFn: ({ userId, value }: { userId: string; value: string }) =>
      walkyAccountsAPI.setPassword(userId, value),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["walky-accounts"] });
      closeDialog();
      setFeedback({
        type: "success",
        message: "Password set. Any existing session on that account was signed out.",
      });
    },
    onError: (
      error: Error & {
        response?: { data?: { message?: string; errors?: string[] } };
      },
    ) => {
      const data = error.response?.data;
      setFormError(data?.errors?.join(" ") ?? data?.message ?? "Could not set the password.");
    },
  });

  const closeDialog = () => {
    setTarget(null);
    setPassword("");
    setConfirmation("");
    setFormError(null);
  };

  const submitPassword = () => {
    setFormError(null);
    if (password !== confirmation) {
      setFormError("The two passwords do not match.");
      return;
    }
    if (!target) return;
    passwordMutation.mutate({ userId: target._id, value: password });
  };

  const missingPassword = (accounts ?? []).filter((a) => !a.has_password).length;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <BadgeCheck className="h-6 w-6" />
              Walky Accounts
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
              One official account per school. Sign into one to post as Walky
              inside that school. Passwords are set here — these accounts cannot
              recover a password by email.
            </p>
          </div>
          <Button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="shrink-0"
          >
            {syncMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Create missing accounts
          </Button>
        </div>

        {feedback && (
          <Alert variant={feedback.type === "error" ? "destructive" : "default"}>
            <AlertDescription>{feedback.message}</AlertDescription>
          </Alert>
        )}

        {missingPassword > 0 && (
          <Alert>
            <AlertDescription>
              {missingPassword} account
              {missingPassword === 1 ? "" : "s"} cannot be signed into yet —
              they have no password.
            </AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Accounts</CardTitle>
            <CardDescription>
              {accounts?.length ?? 0} school
              {accounts?.length === 1 ? "" : "s"} with an official account
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !accounts?.length ? (
              <p className="text-sm text-muted-foreground py-6 text-center">
                No official accounts yet. Use "Create missing accounts" above.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>School</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Sign-in</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts.map((account) => (
                      <TableRow key={account._id}>
                        <TableCell className="font-medium">
                          {account.school?.school_name ?? (
                            <span className="text-muted-foreground">
                              No school
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {account.email}
                        </TableCell>
                        <TableCell>
                          {account.has_password ? (
                            <Badge variant="secondary">Password set</Badge>
                          ) : (
                            <Badge variant="outline">No password</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setTarget(account)}
                          >
                            <KeyRound className="mr-2 h-4 w-4" />
                            {account.has_password
                              ? "Change password"
                              : "Set password"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={!!target} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {target?.has_password ? "Change" : "Set"} password
            </DialogTitle>
            <DialogDescription>
              For <span className="font-mono">{target?.email}</span>. Anyone
              signed into this account will be signed out.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="walky_password">New password</Label>
              <Input
                id="walky_password"
                type="password"
                value={password}
                autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="walky_password_confirm">Confirm</Label>
              <Input
                id="walky_password_confirm"
                type="password"
                value={confirmation}
                autoComplete="new-password"
                onChange={(e) => setConfirmation(e.target.value)}
              />
            </div>
            {formError ? (
              <Alert variant="destructive">
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            ) : null}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={submitPassword}
              disabled={!password || passwordMutation.isPending}
            >
              {passwordMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Save password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default WalkyAccountsPage;
